import React, { useEffect, useState } from 'react';
import Button from '@/components/shared/Button';
import UISpinner from '@/components/shared/UISpinner';
import { getPrivacyPolicy, getTermsOfUse } from '@/api/staticContent';
import './TermsAndConditionsModal.scss';

const baseClass = 'TermsAndConditionsModal';

export interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  type?: 'terms' | 'privacy';
}

interface Props extends TermsAndConditionsModalProps {}

const TermsAndConditionsModal: React.FC<Props> = ({ isOpen, onClose, onAccept, type = 'terms' }) => {
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      const fetchContent = async () => {
        setLoading(true);
        setError(null);
        setHtmlContent('');
        try {
          const apiCall = type === 'terms' ? getTermsOfUse : getPrivacyPolicy;
          const response = await apiCall();
          setHtmlContent(response.data as any);
        } catch (e) {
          setError('Failed to load content. Please try again later.');
          console.error('Failed to fetch static content', e);
        } finally {
          setLoading(false);
        }
      };
      fetchContent();
    }
  }, [isOpen, type]);

  if (!isOpen) return null;

  const isTerms = type === 'terms';
  const title = isTerms ? 'PropSavvy Terms of Service' : 'PropSavvy Privacy Policy';

  const handleAccept = () => {
    if (onAccept) {
      onAccept();
    } else {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={`${baseClass}__backdrop`} onClick={handleBackdropClick}>
      <div className={`${baseClass}__modal`}>
        <div className={`${baseClass}__header`}>
          <div className={`${baseClass}__title-section`}>
            <h2 className={`${baseClass}__title`}>{loading ? 'Loading...' : title}</h2>
          </div>
          <button
            onClick={onClose}
            className={`${baseClass}__close-btn`}
            aria-label={`Close ${isTerms ? 'terms of service' : 'privacy policy'}`}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M10 2L2 10M2 2L10 10" stroke="#919EAB" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className={`${baseClass}__divider`}></div>

        <div className={`${baseClass}__content`}>
          {loading && (
            <div className={`${baseClass}__loader`}>
              <UISpinner />
            </div>
          )}
          {!loading && error && <p className={`${baseClass}__error`}>{error}</p>}
          {!loading && !error && (
            <div
              className={`${baseClass}__text-content`}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          )}
        </div>

        <div className={`${baseClass}__footer`}>
          <div className={`${baseClass}__footer-divider`}></div>
          <p className={`${baseClass}__footer-text`}>
            By clicking "I Agree" or continuing to use our services, you acknowledge that you have read, understood, and agree to be bound by these {isTerms ? 'Terms of Service' : 'Privacy Policy'}.
          </p>
          <div className={`${baseClass}__buttons`}>
            <Button onClick={handleAccept} className={`${baseClass}__agree-btn`}>
              I Agree
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsModal;